'use client';

import type { CurrentUserHrContext } from '@/app/hr/components/types';

interface MeOverviewTabProps { ctx: CurrentUserHrContext; }

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
  <div>
    <label className="block text-[11px] font-semibold text-gray-700 mb-1">{label}</label>
    <p className="px-3 py-2 text-[12px] text-gray-800 rounded-lg bg-gray-50 truncate" title={value}>{value}</p>
  </div>
);

const MeOverviewTab = ({ ctx }: MeOverviewTabProps) => {
  const { employee, supervisor, emergencyContacts, addresses } = ctx;
  const currentAddress = addresses.find(a => a.is_current && a.address_type === 'home') ?? addresses[0];

  return (
    <div className="space-y-5">
      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Personal Information</h3>
        <div className="grid grid-cols-2 gap-3">
          <ReadOnlyField label="Name" value={`${employee.first_name} ${employee.last_name}`} />
          <ReadOnlyField label="Preferred Name" value={employee.preferred_name ?? '—'} />
          <ReadOnlyField label="Pronouns" value={employee.preferred_pronouns ?? '—'} />
          <ReadOnlyField label="Date of Birth" value={employee.date_of_birth ? new Date(employee.date_of_birth).toLocaleDateString() : '—'} />
          <ReadOnlyField label="Personal Email" value={employee.email ?? '—'} />
          <ReadOnlyField label="Personal Phone" value={employee.phone ?? '—'} />
          <ReadOnlyField label="Work Email" value={employee.work_email ?? '—'} />
          <ReadOnlyField label="Work Phone" value={employee.work_phone ?? '—'} />
        </div>
      </section>

      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Employment</h3>
        <div className="grid grid-cols-2 gap-3">
          <ReadOnlyField label="Job Title" value={employee.job_title} />
          <ReadOnlyField label="Department" value={employee.department} />
          <ReadOnlyField label="Employment Type" value={(employee.employment_type ?? '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} />
          <ReadOnlyField label="Hire Date" value={employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'} />
          <ReadOnlyField label="Supervisor" value={supervisor ? `${supervisor.first_name} ${supervisor.last_name} — ${supervisor.job_title}` : 'None'} />
          <ReadOnlyField label="Salary Band" value={employee.salary_band ?? '—'} />
        </div>
      </section>

      {employee.bio && (
        <section>
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Bio</h3>
          <p className="text-[12px] text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-3">{employee.bio}</p>
        </section>
      )}

      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Emergency Contacts</h3>
          <span className="text-[10px] text-gray-400 italic">Edits coming in SS-3</span>
        </div>
        {emergencyContacts.length === 0 ? (
          <p className="text-[12px] text-gray-400 italic">No emergency contacts on file. Contact HR to add.</p>
        ) : (
          <div className="space-y-2">
            {emergencyContacts.map(ec => (
              <div key={ec.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[13px] font-semibold text-[#1C2E5E]">
                      {ec.contact_name}
                      {ec.is_primary && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-700">PRIMARY</span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-500">{ec.relationship}</p>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <a href={`tel:${ec.primary_phone}`} className="text-[#E89A3C] hover:underline">📞 {ec.primary_phone}</a>
                  {ec.email && <a href={`mailto:${ec.email}`} className="text-[#E89A3C] hover:underline truncate">✉ {ec.email}</a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Current Address</h3>
        {!currentAddress ? (
          <p className="text-[12px] text-gray-400 italic">No address on file.</p>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-[12px] text-gray-800 leading-relaxed">
              {currentAddress.street}{currentAddress.street2 ? `, ${currentAddress.street2}` : ''}<br />
              {currentAddress.city}, {currentAddress.state} {currentAddress.zip}
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default MeOverviewTab;
